import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI, Order } from '../../services/api';
import { 
  TruckIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  MapPinIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import OTPDisplay from './OTPDisplay';

const OrdersList: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [regeneratingPayment, setRegeneratingPayment] = useState<number | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await ordersAPI.getUserOrders();
      const ordersData = Array.isArray(response) ? response : (response.orders || []);
      const sortedOrders = ordersData.sort((a: Order, b: Order) => {
        const isPendingPaymentA = a.status === 'pending_payment' || a.status === 'awaiting_payment';
        const isPendingPaymentB = b.status === 'pending_payment' || b.status === 'awaiting_payment';
        if (isPendingPaymentA && !isPendingPaymentB) return -1;
        if (!isPendingPaymentA && isPendingPaymentB) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setOrders(sortedOrders);
    } catch (err: any) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleResumePayment = async (orderId: number) => {
    setRegeneratingPayment(orderId);
    try {
      const result = await ordersAPI.resumePayment(orderId);
      if (result.payment_url) {
        window.open(result.payment_url, '_blank');
        await fetchOrders();
      }
    } catch (error: any) {
      console.error('Failed to regenerate payment link:', error);
      alert(error.response?.data?.detail || 'Failed to regenerate payment link. Please try again.');
    } finally {
      setRegeneratingPayment(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'pending_payment':
      case 'awaiting_payment':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'accepted':
        return <TruckIcon className="h-5 w-5 text-blue-500" />;
      case 'picked_up':
        return <TruckIcon className="h-5 w-5 text-purple-500" />;
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_payment':
      case 'awaiting_payment':
        return 'bg-orange-100 text-orange-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'picked_up':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
              <p className="mt-2 text-gray-600">Track all your delivery orders.</p>
            </div>
            <Link to="/create-order" className="btn-primary">
              Create New Order
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          {orders.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
              <p className="text-gray-500 mb-6">Create your first delivery order to get started.</p>
              <Link to="/create-order" className="btn-primary">
                Create Order
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{order.id}
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <MapPinIcon className="h-4 w-4 text-green-500 mr-1" />
                            <span className="truncate max-w-xs">{order.pickup_location}</span>
                          </div>
                          <div className="flex items-center mt-1">
                            <MapPinIcon className="h-4 w-4 text-red-500 mr-1" />
                            <span className="truncate max-w-xs">{order.dropoff_location}</span>
                          </div>
                          {order.stops && order.stops.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              +{order.stops.length} stop(s)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.package_type}</div>
                        <div className="text-sm text-gray-500">{order.package_weight}kg</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(order.status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₦{order.final_price?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          {(order.status === 'pending_payment' || order.status === 'awaiting_payment') ? (
                            <>
                              {order.payment_url ? (
                                <a
                                  href={order.payment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white px-4 py-2 rounded-volexx font-semibold transition-colors"
                                >
                                  Pay Now
                                </a>
                              ) : null}
                              <button
                                onClick={() => handleResumePayment(Number(order.id))}
                                disabled={regeneratingPayment === Number(order.id)}
                                className="inline-flex items-center gap-1 text-volexx-orange-600 hover:text-volexx-orange-700 font-medium disabled:opacity-50"
                                title="Get a fresh payment link"
                              >
                                <ArrowPathIcon className={`h-4 w-4 ${regeneratingPayment === Number(order.id) ? 'animate-spin' : ''}`} />
                                {regeneratingPayment === Number(order.id) ? 'Loading...' : 'New Link'}
                              </button>
                              <Link
                                to={`/orders/${order.id}`}
                                className="text-volexx-blue-600 hover:text-volexx-blue-700"
                              >
                                View
                              </Link>
                            </>
                          ) : (
                            <Link
                              to={`/orders/${order.id}`}
                              className="text-volexx-blue-600 hover:text-volexx-blue-700"
                            >
                              View Details
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={7} className="p-0">
                        <OTPDisplay orderId={order.id} orderStatus={order.status} />
                      </td>
                    </tr>
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersList;